import React, { useState } from "react"
import {
  UseFormRegister,
  FieldErrors,
  UseFormWatch,
  UseFormSetValue,
} from "react-hook-form"

import { AdvancedTicketCard } from "components/cards/advanced-ticket-card"
import { useOrderTicketQuestions } from "hooks/api/tickets/use-order-ticket-questions"
import { PlaceOrderFormData, Ticket, OrderData } from "types"

interface AdvancedFormsHandlerProps {
  orderData: OrderData | null | undefined
  register: UseFormRegister<PlaceOrderFormData>
  errors: FieldErrors<PlaceOrderFormData>
  watch: UseFormWatch<PlaceOrderFormData>
  setValue: UseFormSetValue<PlaceOrderFormData>
  typeRankOrder?: number[]
}

const AdvancedFormsHandler: React.FC<AdvancedFormsHandlerProps> = ({
  orderData,
  register,
  errors,
  watch,
  setValue,
  typeRankOrder,
}) => {
  // Track "same as contact" state per ticket type
  const [isSameAsContact, setIsSameAsContact] = useState<Record<string, boolean>>({})

  // Get question data for all tickets in the order (memoized internally)
  const { questionsByTicketType } = useOrderTicketQuestions(orderData)

  // Group tickets by type while preserving original order
  const ticketsByType: Record<string, Ticket[]> = {}
  const initialTypeOrder: string[] = []

  orderData?.ticket_set?.forEach((ticket: Ticket) => {
    const typeName = String(ticket.ticket_type)
    if (!ticketsByType[typeName]) {
      ticketsByType[typeName] = []
      initialTypeOrder.push(typeName)
    }
    ticketsByType[typeName].push(ticket)
  })

  // Derive type order based on published ticket type rank order when available
  const typeOrder: string[] = [...initialTypeOrder].sort((a, b) => {
    if (!typeRankOrder || typeRankOrder.length === 0) return 0
    const aPublished = ticketsByType[a]?.[0]?.ticket_type_published
    const bPublished = ticketsByType[b]?.[0]?.ticket_type_published
    const aIdx = aPublished != null ? typeRankOrder.indexOf(aPublished) : -1
    const bIdx = bPublished != null ? typeRankOrder.indexOf(bPublished) : -1
    const aRank = aIdx === -1 ? Number.POSITIVE_INFINITY : aIdx
    const bRank = bIdx === -1 ? Number.POSITIVE_INFINITY : bIdx
    return aRank - bRank
  })

  return (
    <div className="space-y-6">
      {typeOrder.map((typeName) => {
        const ticketsOfType = ticketsByType[typeName]
        const firstTicketOfType = ticketsOfType[0]

        return (
          <div key={typeName} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {ticketsOfType.map((ticket, typeIndex) => {
                const questions = questionsByTicketType.get(
                  ticket.ticket_type_published
                )
                const isFirstOfThisType = ticket.id === firstTicketOfType.id

                return (
                  <AdvancedTicketCard
                    key={ticket.id}
                    ticket={ticket}
                    ticketIndex={typeIndex}
                    register={register}
                    errors={errors}
                    watch={watch}
                    setValue={setValue}
                    isSameAsContact={isSameAsContact[typeName] || false}
                    setIsSameAsContact={(checked) => {
                      setIsSameAsContact((prev) => ({
                        ...prev,
                        [typeName]: checked,
                      }))
                    }}
                    isFirstTicketOfType={isFirstOfThisType}
                    questions={questions}
                  />
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export { AdvancedFormsHandler }
